import { alpha, createTheme } from "@mui/material/styles";

export const focusColors = {
  blue: "#014DDF",
  blueDark: "#0A229C",
  blueIntense: "#0008DE",
  cyan: "#00D9FF",
  violet: "#6C5CE7",
  green: "#1DD1A1",
  red: "#FF4757",
  yellow: "#FDCB6E",
};

export const platformTheme = createTheme({
  palette: {
    primary: {
      main: focusColors.blue,
      dark: focusColors.blueDark,
      light: "#E8F0FF",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: focusColors.violet,
    },
    success: {
      main: focusColors.green,
    },
    error: {
      main: focusColors.red,
    },
    warning: {
      main: focusColors.yellow,
    },
    background: {
      default: "#F5F7FB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#17233C",
      secondary: "#64708A",
    },
    divider: "#E4E9F2",
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
    h1: { fontWeight: 700, letterSpacing: "-0.025em" },
    h2: { fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontWeight: 700, letterSpacing: "-0.018em" },
    h4: { fontWeight: 700, letterSpacing: "-0.015em" },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 10,
          paddingInline: 18,
          textTransform: "none",
          letterSpacing: 0,
        },
        containedPrimary: {
          boxShadow: `0 8px 18px ${alpha(focusColors.blue, 0.2)}`,
          "&:hover": {
            backgroundColor: focusColors.blueDark,
            boxShadow: `0 10px 22px ${alpha(focusColors.blue, 0.28)}`,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(focusColors.blue, 0.42),
          "&:hover": {
            borderColor: focusColors.blue,
            backgroundColor: alpha(focusColors.blue, 0.06),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#FFFFFF",
          transition: "box-shadow 160ms ease, border-color 160ms ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(focusColors.blue, 0.48),
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(focusColors.blue, 0.1)}`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
        },
        notchedOutline: {
          borderColor: "#D9E0EC",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: "1px solid #E4E9F2",
          borderRadius: 12,
          boxShadow: "0 6px 20px rgba(21, 47, 89, 0.06)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: "#E8ECF3",
          paddingBlock: 13,
        },
        head: {
          borderBottom: 0,
          color: "#FFFFFF",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.035em",
          textTransform: "uppercase",
        },
        body: {
          color: "#27344D",
          fontSize: 13,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover:hover": {
            backgroundColor: alpha(focusColors.blue, 0.035),
          },
          "&:last-child td": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "#64708A",
          borderTop: "1px solid #E8ECF3",
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          border: "1px solid #E4E9F2",
          borderRadius: 10,
          backgroundColor: "#FFFFFF",
          boxShadow: "0 2px 8px rgba(21, 47, 89, 0.08)",
        },
        grouped: {
          minWidth: 34,
          border: "0 !important",
          color: "#64708A",
          "&:hover": {
            color: focusColors.blue,
            backgroundColor: alpha(focusColors.blue, 0.06),
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: "1px solid #E4E9F2",
          borderRadius: 16,
          boxShadow: "0 24px 70px rgba(20, 43, 83, 0.2)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 0,
          boxShadow: "10px 0 34px rgba(20, 43, 83, 0.08)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          fontWeight: 600,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 42,
          borderRadius: 8,
          marginInline: 6,
          "&.Mui-selected": {
            color: focusColors.blueDark,
            backgroundColor: alpha(focusColors.blue, 0.08),
          },
        },
      },
    },
  },
});
